import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
    api as axiosApi, // axios instance with /api base
} from "@/lib/api";
import { listCampaigns, STATUS_COLORS } from "@/lib/chiroApi";
import { ShieldCheck, ArrowLeft, Search, MapPin } from "lucide-react";

export default function ProspectReceipt() {
    const { id } = useParams();
    const [prospect, setProspect] = useState(null);
    const [campaign, setCampaign] = useState(null);
    const [err, setErr] = useState(null);

    // Internal receipt — never indexed, even though it's gated to operators only.
    useEffect(() => {
        const m = document.createElement("meta");
        m.name = "robots";
        m.content = "noindex,nofollow";
        document.head.appendChild(m);
        const prevTitle = document.title;
        document.title = "Internal Receipt · ProofCommand";
        return () => {
            document.head.removeChild(m);
            document.title = prevTitle;
        };
    }, []);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await axiosApi.get(`/prospects/${id}`);
                if (!alive) return;
                setProspect(data);
                if (data?.campaign_id) {
                    const all = await listCampaigns();
                    if (!alive) return;
                    setCampaign(all.find((c) => c.id === data.campaign_id) || null);
                }
            } catch (e) {
                if (alive) setErr(e?.response?.status === 404 ? "not_found" : "error");
            }
        })();
        return () => {
            alive = false;
        };
    }, [id]);

    if (err === "not_found")
        return (
            <Wrap>
                <NotFound />
            </Wrap>
        );
    if (err)
        return (
            <Wrap>
                <div className="font-mono text-[#ff453a]">load error</div>
            </Wrap>
        );
    if (!prospect)
        return (
            <Wrap>
                <div className="font-mono text-[#9ca3af]">loading…</div>
            </Wrap>
        );

    const wasEdited =
        (prospect.original_generated_visibility_issue ?? "") !==
            (prospect.visibility_issue ?? "") ||
        (prospect.original_generated_proof_angle ?? "") !== (prospect.proof_angle ?? "") ||
        (prospect.original_generated_draft_email ?? "") !== (prospect.draft_email ?? "");

    return (
        <Wrap>
            <div className="pc-fade-up" data-testid="prospect-receipt-page">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <Link
                        to="/chirotracker"
                        className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#9ca3af] hover:text-[#7cc93c]"
                    >
                        <ArrowLeft className="w-3 h-3" /> back to ChiroTracker
                    </Link>
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9ca3af]">
                        receipt id · {prospect.id}
                    </span>
                </div>

                {/* Internal banner — explicit, prominent */}
                <div
                    className="border border-[#ff9f0a]/40 bg-[#ff9f0a]/5 px-5 py-3 mb-6 flex items-center gap-3"
                    data-testid="internal-banner"
                >
                    <ShieldCheck className="w-4 h-4 text-[#ff9f0a]" />
                    <div className="font-mono text-[11px] tracking-[0.05em] text-[#ff9f0a]">
                        Internal proof receipt — not customer-facing
                    </div>
                </div>

                {/* Title block */}
                <div className="mb-8">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7cc93c] mb-2">
                        // proof trail · how this draft was made
                    </div>
                    <h1 className="text-3xl font-bold tracking-tight">
                        {prospect.practice_name || "(untitled)"}
                    </h1>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span
                            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-[2px] text-[10px] uppercase tracking-[0.15em] border"
                            style={{
                                color: STATUS_COLORS[prospect.status] || "#9ca3af",
                                borderColor: `${STATUS_COLORS[prospect.status] || "#9ca3af"}55`,
                                background: `${STATUS_COLORS[prospect.status] || "#9ca3af"}15`,
                            }}
                        >
                            <span
                                className="w-1.5 h-1.5 rounded-full"
                                style={{ background: STATUS_COLORS[prospect.status] || "#9ca3af" }}
                            />
                            {prospect.status}
                        </span>
                        {prospect.safety_reviewed_at && (
                            <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#7cc93c] border border-[#7cc93c]/30 bg-[#7cc93c]/5 px-2 py-0.5 rounded-[2px]">
                                <ShieldCheck className="w-3 h-3" /> safety reviewed
                            </span>
                        )}
                        {wasEdited && (
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff9f0a] border border-[#ff9f0a]/30 bg-[#ff9f0a]/10 px-2 py-0.5 rounded-[2px]">
                                edited for claim safety
                            </span>
                        )}
                    </div>
                </div>

                {/* Identity grid */}
                <Section title="prospect">
                    <KV label="Practice" value={prospect.practice_name} />
                    <KV label="Campaign" value={campaign?.name || prospect.workflow_name || "—"} />
                    <KV
                        label="Location"
                        value={[prospect.city, prospect.state, prospect.zip]
                            .filter(Boolean)
                            .join(" · ") || "—"}
                    />
                    <KV label="Website" value={prospect.website || "—"} mono />
                    <KV label="Contact" value={prospect.contact_name || "—"} />
                </Section>

                {/* Proof links */}
                <Section title="proof context · live links">
                    <div className="grid grid-cols-2 gap-2 mb-3">
                        <ReceiptLink
                            href={prospect.google_search_url}
                            icon={Search}
                            label="open google search"
                        />
                        <ReceiptLink
                            href={prospect.google_maps_search_url}
                            icon={MapPin}
                            label="open google maps search"
                        />
                    </div>
                    <KV
                        label="Search URL"
                        value={prospect.google_search_url || "—"}
                        mono
                        wrap
                    />
                    <KV
                        label="Maps URL"
                        value={prospect.google_maps_search_url || "—"}
                        mono
                        wrap
                    />
                    <Block label="Operator notes (primary source of truth)">
                        {prospect.proof_context_notes || "(none)"}
                    </Block>
                    <KV
                        label="Last manually checked"
                        value={fmt(prospect.last_proof_checked_at)}
                        mono
                    />
                </Section>

                {/* Originals */}
                <Section
                    title="original AI drafts (receipts)"
                    sub="What the model produced before the safety pass."
                >
                    <Block
                        label="Original visibility_issue"
                        testid="original-issue"
                    >
                        {prospect.original_generated_visibility_issue || "(not generated yet)"}
                    </Block>
                    <Block label="Original proof_angle" testid="original-angle">
                        {prospect.original_generated_proof_angle || "(not generated yet)"}
                    </Block>
                    <Block label="Original draft_email" testid="original-email">
                        {prospect.original_generated_draft_email || "(not generated yet)"}
                    </Block>
                </Section>

                {/* Cleaned active */}
                <Section
                    title="cleaned · active outreach copy"
                    sub="What the team uses. Equal to the originals if no edits were needed."
                >
                    <Block label="visibility_issue (active)" testid="active-issue">
                        {prospect.visibility_issue || "(empty)"}
                    </Block>
                    <Block label="proof_angle (active)" testid="active-angle">
                        {prospect.proof_angle || "(empty)"}
                    </Block>
                    <Block label="draft_outreach_email (active)" testid="active-email">
                        {prospect.draft_email || "(empty)"}
                    </Block>
                </Section>

                {/* Safety review */}
                <Section title="safety review">
                    <KV label="Safety reviewed at" value={fmt(prospect.safety_reviewed_at)} mono />
                    <Block label="Safety review notes" testid="safety-notes">
                        {prospect.safety_review_notes || "(no review yet)"}
                    </Block>
                </Section>

                <div className="mt-12 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-[#9ca3af]">
                    no claims · receipts · proofcommand internal
                </div>
            </div>
        </Wrap>
    );
}

/* helpers */
function Wrap({ children }) {
    return <div className="max-w-3xl mx-auto">{children}</div>;
}

function NotFound() {
    return (
        <div className="pc-fade-up py-16 text-center" data-testid="receipt-not-found">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#ff453a] mb-2">
                404
            </div>
            <div className="text-2xl font-bold tracking-tight mb-2">Prospect not found</div>
            <Link
                to="/chirotracker"
                className="font-mono text-[11px] text-[#9ca3af] hover:text-[#7cc93c]"
            >
                ← back to ChiroTracker
            </Link>
        </div>
    );
}

function Section({ title, sub, children }) {
    return (
        <div className="mb-8 border border-[#262a2e] bg-[#111315]">
            <div className="px-5 py-3 border-b border-[#262a2e]">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9ca3af]">
                    {title}
                </div>
                {sub && (
                    <div className="font-mono text-[10px] text-[#9ca3af] mt-1 normal-case tracking-normal">
                        {sub}
                    </div>
                )}
            </div>
            <div className="p-5">{children}</div>
        </div>
    );
}

function KV({ label, value, mono, wrap }) {
    return (
        <div className="grid grid-cols-[160px_1fr] gap-4 py-1.5 border-b last:border-b-0 border-[#262a2e]">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9ca3af]">
                {label}
            </div>
            <div
                className={`text-[12px] ${mono ? "font-mono" : "font-sans"} text-[#f3f4f6] ${
                    wrap ? "break-all" : "truncate"
                }`}
            >
                {value || "—"}
            </div>
        </div>
    );
}

function Block({ label, children, testid }) {
    return (
        <div className="my-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9ca3af] mb-1.5">
                {label}
            </div>
            <pre
                data-testid={testid}
                className="bg-[#090a0b] border border-[#262a2e] p-3 font-mono text-[11px] leading-relaxed text-[#f3f4f6] whitespace-pre-wrap break-words max-h-72 overflow-auto"
            >
                {children}
            </pre>
        </div>
    );
}

function ReceiptLink({ href, icon: Icon, label }) {
    const enabled = !!href;
    return (
        <a
            href={enabled ? href : "#"}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => {
                if (!enabled) e.preventDefault();
            }}
            aria-disabled={!enabled}
            className={`inline-flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] border px-3 py-2 rounded-[2px] transition-colors ${
                enabled
                    ? "border-[#262a2e] text-[#9ca3af] hover:border-[#7cc93c] hover:text-[#7cc93c]"
                    : "border-[#1c1f22] text-[#3a3f44] cursor-not-allowed"
            }`}
        >
            <Icon className="w-3 h-3" /> {label}
        </a>
    );
}

function fmt(iso) {
    if (!iso) return "—";
    try {
        return iso.replace("T", " ").slice(0, 19) + "Z";
    } catch {
        return iso;
    }
}
